'use client'

import {
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type KeyboardEvent
} from 'react'
import Link from 'next/link'
import Image from 'next/image'
import useSWR from 'swr'
import { NinTaglinePong } from './nin-tagline-pong'
import { NinCraftNamaste } from './nin-craft-namaste'
import { fetchLandingHeroDemoSrc } from '@/lib/cms/landing-hero'
import { NIN_HERO_DEMO_SRC } from '@/lib/landing-demos'
import {
  motion,
  useReducedMotion,
  useScroll,
  useTransform
} from 'framer-motion'
import {
  ArrowDown,
  AudioLines,
  BookOpen,
  CalendarClock,
  Layers3,
  MessageSquare,
  NotebookPen,
  PenLine,
  Users,
  ArrowRight,
  ArrowUpRight,
  Check,
  Code2,
  FileText,
  Globe2,
  LockKeyhole,
  Menu,
  Mic,
  Play,
  Plus,
  ShieldCheck,
  Workflow,
  X
} from 'lucide-react'
import { CmsLandingHighlights } from '@/app/(marketing)/_components/cms/cms-landing-highlights'
import {
  mailtoEnterprise,
  mailtoSupport
} from '@/app/(marketing)/_components/nin-contact-links'
import {
  NIN_BLOGS_URL,
  NIN_CONNECT_PLATFORM_URL,
  NIN_GITHUB_URL,
  NIN_LEARN_URL,
  NIN_MCP_SERVER_URL,
  NIN_TELEGRAM_COMMUNITY_URL,
  NIN_TWITTER_URL,
  NIN_WEBUPI_DOCS_URL,
  NINXL_DOWNLOAD_HREF
} from './nin-org-links'

const surfaces = [
  {
    id: 'home',
    icon: MessageSquare,
    label: 'Ask',
    title: 'A place for every thought.',
    description:
      'Chat with a local model. Bring your files, switch on the tools you need, and start working.'
  },
  {
    id: 'studio',
    icon: Layers3,
    label: 'Create',
    title: 'Give your ideas a little room.',
    description:
      'Bring your creative work into Studio. Keep your tools and your next idea in one workspace.'
  },
  {
    id: 'crew',
    icon: Users,
    label: 'Delegate',
    title: 'A little help goes a long way.',
    description:
      'Organize agents in Crew and give each one a role in the work ahead.'
  },
  {
    id: 'automations',
    icon: CalendarClock,
    label: 'Automate',
    title: 'Make the everyday easier.',
    description:
      'Set up repeatable tasks in Automations. Spend less time starting the same work again.'
  },
  {
    id: 'voice',
    icon: AudioLines,
    label: 'Voice',
    title: 'Think out loud.',
    description:
      'Bring your voice into the conversation. Speak naturally and keep your train of thought.'
  },
  {
    id: 'inkflow',
    icon: PenLine,
    label: 'InkFlow',
    title: 'Your voice. Your words. Anywhere.',
    description:
      'Dictate into the apps you already use. Turn a spoken thought into writing with NINK’s InkFlow.'
  },
  {
    id: 'notes',
    icon: NotebookPen,
    label: 'Notes',
    title: 'Be in the conversation. Keep what matters.',
    description:
      'Transcribe calls with consent, then turn your transcript and notes into key points, decisions, and action items. Choose an on-device model to keep your summaries private and local.'
  }
] as const

function FilmPlayer({
  open,
  onClose,
  demoSrc
}: {
  open: boolean
  onClose: () => void
  demoSrc?: string
}) {
  const dialog = useRef<HTMLDialogElement>(null)
  useEffect(() => {
    const node = dialog.current
    if (!node) return
    if (open && !node.open) node.showModal()
    if (!open && node.open) node.close()
    if (!open) return
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [open])
  return (
    <dialog
      ref={dialog}
      className="nf-film"
      aria-labelledby={demoSrc ? 'nf-demo-title' : 'nf-film-title'}
      onClose={onClose}
    >
      <div className="nf-film-inner">
        <div className="nf-film-top">
          <h2 id={demoSrc ? 'nf-demo-title' : 'nf-film-title'}>
            {demoSrc ? 'NIN — See it in action' : 'NINX — The original film'}
          </h2>
          <button
            type="button"
            aria-label={demoSrc ? 'Close demo' : 'Close film'}
            onClick={onClose}
          >
            <X aria-hidden="true" />
          </button>
        </div>
        {open ? (
          <video
            controls
            autoPlay
            playsInline
            preload="metadata"
            poster={demoSrc ? '/nin/film/home.jpg' : '/nin/film/v1-poster.jpg'}
            src={demoSrc ?? '/nin/film/ninx-v1.mp4'}
            aria-label={
              demoSrc
                ? 'NIN product demo'
                : 'NINX original launch film, 32 seconds'
            }
          />
        ) : null}
        <p>
          {demoSrc
            ? 'The original NIN product walkthrough.'
            : 'NINX launch film · Version 1 · 00:32'}
        </p>
      </div>
    </dialog>
  )
}

function ProductDemo() {
  const [open, setOpen] = useState(false)
  const { data: demoSrc } = useSWR(
    'nin-film-landing-demo',
    () => fetchLandingHeroDemoSrc('nin'),
    { fallbackData: NIN_HERO_DEMO_SRC, revalidateOnFocus: false }
  )
  return (
    <section
      id="demo"
      className="nf-demo nf-section"
      aria-labelledby="nf-demo-heading"
    >
      <div className="nf-section-top">
        <span className="nf-eyebrow">THE PRODUCT, IN PRACTICE</span>
        <span className="nf-note">A closer look at what comes next.</span>
      </div>
      <div className="nf-section-heading">
        <h2 id="nf-demo-heading">
          Don’t just imagine it.
          <br />
          <span>See it happen.</span>
        </h2>
        <p>
          The original product demo. See how NIN brings your voice, browser, and
          work together.
        </p>
      </div>
      <button
        type="button"
        className="nf-demo-preview"
        onClick={() => setOpen(true)}
        aria-label="Play NIN hero demo"
      >
        <Image
          src="/nin/film/home.jpg"
          width={1600}
          height={1000}
          alt=""
          unoptimized
        />
        <span className="nf-demo-play">
          <Play fill="currentColor" size={20} aria-hidden="true" /> Play demo{' '}
          <ArrowUpRight size={20} aria-hidden="true" />
        </span>
      </button>
      <FilmPlayer
        open={open}
        onClose={() => setOpen(false)}
        demoSrc={demoSrc}
      />
    </section>
  )
}

function Lessons() {
  return (
    <section
      id="lessons"
      className="nf-lessons nf-section"
      aria-labelledby="nf-lessons-heading"
    >
      <div className="nf-section-top">
        <span className="nf-eyebrow">05 / KEEP EXPLORING</span>
        <span className="nf-note">A little knowledge goes a long way.</span>
      </div>
      <div className="nf-section-heading">
        <h2 id="nf-lessons-heading">
          A place to learn.
          <br />
          <span>And a reason to try.</span>
        </h2>
        <p>
          Lessons, ideas, and notes from the people building NIN. Start wherever
          curiosity takes you.
        </p>
      </div>
      <div className="nf-learning-links">
        <Link href={NIN_LEARN_URL}>
          <BookOpen
            className="nf-learning-icon"
            size={28}
            strokeWidth={1.5}
            aria-hidden="true"
          />
          <span className="nf-eyebrow">LEARN AT YOUR OWN PACE</span>
          <h3>Lessons</h3>
          <p>Explore the learning library.</p>
          <ArrowUpRight aria-hidden="true" />
        </Link>
        <Link href={NIN_BLOGS_URL}>
          <NotebookPen
            className="nf-learning-icon"
            size={28}
            strokeWidth={1.5}
            aria-hidden="true"
          />
          <span className="nf-eyebrow">FROM INSIDE NIN</span>
          <h3>Field notes</h3>
          <p>Read the latest stories and updates.</p>
          <ArrowUpRight aria-hidden="true" />
        </Link>
      </div>
      <div className="nf-editorial">
        <CmsLandingHighlights />
      </div>
    </section>
  )
}

function InkFlowPreview() {
  return (
    <div className="nf-inkflow-preview">
      <div className="nf-inkflow-notch" aria-hidden="true">
        <Mic size={20} />
        <span>Dictate</span>
        <span className="nf-inkflow-bars">▂ ▅ ▃ ▇ ▄ ▂</span>
        <span>0:08</span>
      </div>
      <span className="nf-eyebrow">INKFLOW / ACROSS YOUR APPS</span>
      <h3>
        Less typing.
        <br />
        <em>More you.</em>
      </h3>
      <p>Speak at your cursor. Shape it with a command.</p>
      <div className="nf-inkflow-shortcuts">
        <div>
          <kbd>⇧ Shift</kbd>
          <strong>Dictate</strong>
          <span>Your speech, written.</span>
        </div>
        <div>
          <kbd>⇪ Caps Lock</kbd>
          <strong>Instruct</strong>
          <span>Say what to write or rewrite.</span>
        </div>
        <div>
          <kbd>Right ⌘</kbd>
          <strong>Ask aloud</strong>
          <span>Hear an answer back.</span>
        </div>
      </div>
      <small>
        Illustrative HUD · macOS default shortcuts · Configurable in Settings
      </small>
    </div>
  )
}

function NotesPreview() {
  return (
    <div className="nf-notes-preview">
      <Image
        src="/nin/film/notes.jpg"
        alt="NINX Notes workspace"
        width={1440}
        height={900}
        sizes="(max-width: 760px) 94vw, 86vw"
        unoptimized
      />
      <div className="nf-notes-hud" aria-label="Illustrative meeting notes HUD">
        <div className="nf-notes-hud-row">
          <Mic size={23} aria-hidden="true" />
          <div>
            <strong>Start AI Meeting Note</strong>
            <span>Transcribing opens NINX</span>
          </div>
          <span className="nf-notes-hud-action">Start transcribing</span>
        </div>
        <p>
          By starting, you confirm everyone being transcribed has given consent.
        </p>
        <small>HUD preview · No recording is active</small>
      </div>
    </div>
  )
}

export function Workspace({ downloadHref }: { downloadHref?: string } = {}) {
  const [active, setActive] = useState(0)
  const current = surfaces[active]
  const tabs = useRef<(HTMLButtonElement | null)[]>([])
  const tabRail = useRef<HTMLDivElement>(null)
  const reduceMotion = useReducedMotion()
  function selectTab(index: number) {
    setActive(index)
    const rail = tabRail.current
    const tab = tabs.current[index]
    if (!rail || !tab) return
    rail.scrollTo?.({
      left:
        tab.offsetLeft -
        rail.offsetLeft -
        (rail.clientWidth - tab.clientWidth) / 2,
      behavior: reduceMotion ? 'auto' : 'smooth'
    })
  }
  function onKey(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    let next = index
    if (event.key === 'ArrowRight') next = (index + 1) % surfaces.length
    else if (event.key === 'ArrowLeft')
      next = (index + surfaces.length - 1) % surfaces.length
    else if (event.key === 'Home') next = 0
    else if (event.key === 'End') next = surfaces.length - 1
    else return
    event.preventDefault()
    selectTab(next)
    tabs.current[next]?.focus({ preventScroll: true })
  }
  return (
    <section
      id="workspace"
      className="nf-workspace nf-section"
      aria-labelledby="nf-workspace-heading"
    >
      <div className="nf-section-top">
        <span className="nf-eyebrow">01 / THE WORKSPACE</span>
        <span className="nf-note">Less switching. More doing.</span>
      </div>
      <div className="nf-section-heading">
        <h2 id="nf-workspace-heading">
          One place.
          <br />
          <span>So many possibilities.</span>
        </h2>
        <p>
          From the first question to the finished work. A private desktop
          workspace that moves with you.
        </p>
      </div>
      <div
        className="nf-tablist"
        ref={tabRail}
        role="tablist"
        aria-label="Explore NINX capabilities"
      >
        {surfaces.map((surface, index) => (
          <button
            key={surface.id}
            ref={node => {
              tabs.current[index] = node
            }}
            type="button"
            role="tab"
            id={`nf-tab-${surface.id}`}
            aria-controls={`nf-panel-${surface.id}`}
            aria-selected={active === index}
            tabIndex={active === index ? 0 : -1}
            onClick={() => selectTab(index)}
            onKeyDown={event => onKey(event, index)}
          >
            <surface.icon
              size={18}
              strokeWidth={1.6}
              className="nf-capability-icon"
              aria-hidden="true"
            />
            {surface.label}
            <ArrowUpRight size={16} aria-hidden="true" />
          </button>
        ))}
      </div>
      <div className="nf-tab-guide">
        <span>Explore all seven ways to work</span>
        <span>
          Swipe or use arrow keys <ArrowRight size={14} aria-hidden="true" />
        </span>
      </div>
      {surfaces.map((surface, index) => (
        <div
          key={surface.id}
          className="nf-window"
          role="tabpanel"
          id={`nf-panel-${surface.id}`}
          aria-labelledby={`nf-tab-${surface.id}`}
          tabIndex={0}
          hidden={active !== index}
        >
          <div className="nf-window-bar" aria-hidden="true">
            <span>
              <i />
              <i />
              <i />
            </span>
            <span>NINX / {surface.label}</span>
            <LockKeyhole size={12} />
          </div>
          {active === index ? (
            surface.id === 'inkflow' ? (
              <InkFlowPreview />
            ) : surface.id === 'notes' ? (
              <NotesPreview />
            ) : (
              <Image
                src={`/nin/film/${surface.id}.jpg`}
                alt={`NINX ${surface.label} workspace`}
                width={1600}
                height={1000}
                sizes="(max-width: 760px) 94vw, 86vw"
                unoptimized
              />
            )
          ) : null}
        </div>
      ))}
      <div className="nf-workspace-caption" aria-live="polite">
        <h3>{current.title}</h3>
        <p>{current.description}</p>
        <Link
          href={current.id === 'inkflow' ? '/nink' : (downloadHref ?? '/ninxl')}
        >
          {current.id === 'inkflow'
            ? 'Explore InkFlow'
            : downloadHref
              ? 'Get NINX'
              : 'Meet NINX'}{' '}
          <ArrowUpRight size={18} aria-hidden="true" />
        </Link>
      </div>
    </section>
  )
}

function Privacy() {
  const [permissions, setPermissions] = useState([true, false, false])
  const labels = ['Memory', 'Web', 'Code']
  const icons = [FileText, Globe2, Code2]
  return (
    <section
      id="privacy"
      className="nf-privacy nf-section"
      aria-labelledby="nf-privacy-heading"
    >
      <div className="nf-privacy-copy">
        <span className="nf-eyebrow">02 / PRIVATE BY DESIGN</span>
        <h2 id="nf-privacy-heading">
          Your tools.
          <br />
          <span>Your call.</span>
        </h2>
        <p>
          Intelligence should feel personal.
          <br />
          So should control.
        </p>
        <div className="nf-privacy-facts">
          <div>
            <LockKeyhole size={20} aria-hidden="true" />
            <p>
              <strong>Local, by default.</strong>
              <span>Your models and files can stay on your device.</span>
            </p>
          </div>
          <div>
            <Check size={20} aria-hidden="true" />
            <p>
              <strong>Connected, by choice.</strong>
              <span>
                Choose your integrations. Connected services follow their own
                data policies.
              </span>
            </p>
          </div>
        </div>
        <Link className="nf-text-link" href="/ninxl">
          Explore local-first AI <ArrowUpRight size={18} aria-hidden="true" />
        </Link>
      </div>
      <div className="nf-privacy-art">
        <Image
          src="/nin/film/wall.jpg"
          alt="Soft plant shadows in natural light"
          fill
          sizes="(max-width: 760px) 100vw, 50vw"
          unoptimized
        />
        <span className="nf-art-label">A little space. All yours.</span>
        <div className="nf-glass-controls">
          <div className="nf-glass-heading">
            <span className="nf-brand">
              NINX<span>.</span>
            </span>
            <LockKeyhole size={18} aria-hidden="true" />
          </div>
          {labels.map((label, index) => {
            const Icon = icons[index]
            return (
              <div className="nf-permission" key={label}>
                <span>
                  <Icon size={20} strokeWidth={1.5} aria-hidden="true" />
                  {label}
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-label={`${label} preview`}
                  aria-checked={permissions[index]}
                  onClick={() =>
                    setPermissions(previous =>
                      previous.map((value, i) => (i === index ? !value : value))
                    )
                  }
                >
                  <span />
                </button>
              </div>
            )
          })}
          <p aria-live="polite">
            {permissions.some(Boolean)
              ? `${labels.filter((_, i) => permissions[i]).join(', ')} enabled in this preview.`
              : 'All tools off. The choice is yours.'}
          </p>
        </div>
        <span className="nf-art-footnote">
          Interactive preview · Your app settings are unchanged.
        </span>
      </div>
    </section>
  )
}

export function NinFilmLanding({
  modelsPreview
}: {
  modelsPreview?: ReactNode
}) {
  const [filmOpen, setFilmOpen] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const menuButton = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    if (!menuOpen) return
    const closeOnEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMenuOpen(false)
        menuButton.current?.focus()
      }
    }
    window.addEventListener('keydown', closeOnEscape)
    return () => window.removeEventListener('keydown', closeOnEscape)
  }, [menuOpen])
  const hero = useRef<HTMLElement>(null)
  const root = useRef<HTMLDivElement>(null)
  const reduceMotion = useReducedMotion()
  const { scrollYProgress } = useScroll({
    target: hero,
    offset: ['start start', 'end start']
  })
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 100])
  const heroScale = useTransform(scrollYProgress, [0, 0.8], [1, 0.94])
  useEffect(() => {
    const node = root.current
    if (!node || !('IntersectionObserver' in window)) return
    const observer = new IntersectionObserver(
      entries =>
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('nf-in-view')
            observer.unobserve(entry.target)
          }
        }),
      { threshold: 0.12 }
    )
    node
      .querySelectorAll('.nf-section')
      .forEach(section => observer.observe(section))
    return () => observer.disconnect()
  }, [])
  return (
    <div
      className="nf-experience nm-landing"
      ref={root}
      data-testid="nin-film-shell"
    >
      <a href="#main" className="nf-skip">
        Skip to content
      </a>
      <header className="nf-header">
        <Link
          href="/nin"
          aria-label="NIN home"
          className="nf-brand nf-nav-brand"
        >
          <Image
            src="/ninx_logo_light.svg"
            alt=""
            width={32}
            height={32}
            unoptimized
          />
          <span className="nf-wordmark">NIN</span>
        </Link>
        <nav
          aria-label="NIN organization"
          className={menuOpen ? 'nf-nav nf-nav-open' : 'nf-nav'}
          id="nf-navigation"
        >
          {[
            ['Models', '/nin/models'],
            ['Projects', '#projects'],
            ['Our approach', '#privacy'],
            ['Connect', '#connect-platform'],
            ['Lessons', '#lessons'],
            ['Contact', '#contact']
          ].map(([name, href]) => (
            <a href={href} key={href} onClick={() => setMenuOpen(false)}>
              {name}
            </a>
          ))}
        </nav>
        <Link href={NINXL_DOWNLOAD_HREF} className="nf-button nf-nav-cta">
          Get NINX <ArrowUpRight size={16} aria-hidden="true" />
        </Link>
        <button
          type="button"
          className="nf-menu"
          ref={menuButton}
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={menuOpen}
          aria-controls="nf-navigation"
          onClick={() => setMenuOpen(value => !value)}
        >
          {menuOpen ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
        </button>
      </header>
      <main id="main">
        <section className="nf-hero" ref={hero} aria-labelledby="nf-hero-title">
          <div className="nf-hero-top">
            <div className="nf-brand-game nin-craft-pong">
              <NinTaglinePong headingAs="p" />
            </div>
            <span className="nf-note">Focused models. Private workflows.</span>
          </div>
          <motion.div
            className="nf-hero-type"
            style={reduceMotion ? undefined : { y: heroY, scale: heroScale }}
          >
            <h1
              id="nf-hero-title"
              className="nf-hero-headline nm-landing-headline"
            >
              <span>Private AI.</span>
              <span>
                Focused <em>models.</em>
              </span>
              <span>Built by NIN.</span>
            </h1>
          </motion.div>
          <div className="nf-hero-bottom">
            <p>
              Private AI for enterprises, government and banks.
              <br />
              Specialized models for institutional workflows. Explore available
              workflows in the NINXL desktop app.
            </p>
            <div className="nf-hero-actions">
              <Link className="nf-button" href="/nin/models">
                Explore models{' '}
                <ArrowUpRight size={18} aria-hidden="true" />
              </Link>
              <a
                className="nf-text-link"
                href={mailtoEnterprise('NIN models pilot inquiry')}
              >
                Discuss a pilot <ArrowUpRight size={18} aria-hidden="true" />
              </a>
            </div>
          </div>
          <div className="nf-hero-index">
            <span>
              <ShieldCheck size={15} strokeWidth={1.5} aria-hidden="true" />{' '}
              REDACT / PRIVACY
            </span>
            <span>
              <AudioLines size={15} strokeWidth={1.5} aria-hidden="true" />{' '}
              TRANSCRIBE / VOICE
            </span>
            <span>
              <Workflow size={15} strokeWidth={1.5} aria-hidden="true" /> ACTION
              / WORKFLOWS
            </span>
            <button
              type="button"
              className="nf-film-button"
              onClick={() => setFilmOpen(true)}
            >
              Watch the film <Play size={13} aria-hidden="true" />
            </button>
            <a href="#workspace" aria-label="Explore the workspace">
              <ArrowDown size={19} aria-hidden="true" />
            </a>
          </div>
        </section>
        {modelsPreview}
        <section
          className="nf-hands"
          aria-label="A sanctuary for you and your data"
        >
          <NinCraftNamaste />
        </section>
        <Workspace />
        <ProductDemo />
        <section
          className="nf-statement nf-section"
          aria-label="Our philosophy"
        >
          <span className="nf-eyebrow">
            A DIFFERENT KIND OF RELATIONSHIP WITH AI
          </span>
          <p>
            A little less busy.
            <br />
            <span>
              A lot more <em>you.</em>
            </span>
          </p>
          <div>
            Useful by nature. Private by design.
            <br />
            Open source at heart.
          </div>
        </section>
        <Privacy />
        <section
          id="projects"
          className="nf-products nf-section"
          aria-labelledby="nf-products-heading"
        >
          <div className="nf-section-top">
            <span className="nf-eyebrow">03 / THE FAMILY</span>
            <span className="nf-note">Different moments. The same belief.</span>
          </div>
          <div className="nf-section-heading">
            <h2 id="nf-products-heading">
              Find your
              <br />
              <span>kind of flow.</span>
            </h2>
            <p>
              A focused tool for your desktop, your voice, and wherever the day
              takes you.
            </p>
          </div>
          <div className="nf-product-grid">
            <article className="nf-product nf-product-primary">
              <div className="nf-product-top">
                <span className="nf-eyebrow">01 / DESKTOP</span>
                <span className="nf-product-chip">LOCAL-FIRST</span>
              </div>
              <h3>
                NINX<span>.</span>
              </h3>
              <p>
                Your thinking space.
                <br />
                Now a doing space.
              </p>
              <Link href="/ninxl" className="nf-product-link">
                Meet NINX <ArrowUpRight aria-hidden="true" />
              </Link>
              <Image
                src="/nin/film/home.jpg"
                alt="NINX private desktop workspace"
                width={1600}
                height={1000}
                sizes="(max-width: 760px) 90vw, 50vw"
                unoptimized
              />
            </article>
            <article className="nf-product nf-product-voice">
              <div className="nf-product-top">
                <span className="nf-eyebrow">02 / VOICE</span>
                <Mic size={20} aria-hidden="true" />
              </div>
              <h3>
                NINK<span>.</span>
              </h3>
              <p>
                Say what you mean.
                <br />
                Write where you want.
              </p>
              <div className="nf-voice-line" aria-hidden="true">
                {[
                  12, 22, 34, 19, 44, 58, 29, 46, 64, 40, 26, 52, 36, 18, 30, 12
                ].map((height, index) => (
                  <i
                    key={index}
                    style={{ height, animationDelay: `${index * -0.13}s` }}
                  />
                ))}
              </div>
              <Link href="/nink" className="nf-product-link">
                Find your voice <ArrowUpRight aria-hidden="true" />
              </Link>
            </article>
            <article className="nf-product nf-product-mobile">
              <div className="nf-product-top">
                <span className="nf-eyebrow">03 / MOBILE</span>
                <ArrowUpRight size={20} aria-hidden="true" />
              </div>
              <h3>
                Ningenie<span>.</span>
              </h3>
              <div className="nf-mobile-showcase">
                <p>
                  Less app hopping.
                  <br />
                  More life happening.
                </p>
                <Image
                  src="/nin/film/ningenie-studio-transparent.png"
                  alt="NINGENIE Studio on mobile, showing creative workflows in the app"
                  width={883}
                  height={1782}
                  sizes="(max-width: 760px) 42vw, 200px"
                  className="nf-mobile-mock"
                />
              </div>
              <Link href="/home" className="nf-product-link">
                Take it with you <ArrowUpRight aria-hidden="true" />
              </Link>
            </article>
          </div>
          <div className="nf-project-foot">
            <span>Built in the open. Made to make a difference.</span>
            <a href={NIN_GITHUB_URL} target="_blank" rel="noopener noreferrer">
              Explore the source <ArrowUpRight size={17} aria-hidden="true" />
            </a>
          </div>
        </section>
        <section
          id="connect-platform"
          className="nf-connect nf-section"
          aria-labelledby="nf-connect-heading"
        >
          <div className="nf-connect-heading">
            <span className="nf-eyebrow">04 / CONNECT, ON YOUR TERMS</span>
            <h2 id="nf-connect-heading">
              Your world.
              <br />
              <span>Working together.</span>
            </h2>
            <p>
              Bring your apps into the conversation. Connect email, calendars,
              developer tools, and MCP servers through an open-source platform
              you can self-host.
            </p>
            <a
              className="nf-button"
              href={NIN_CONNECT_PLATFORM_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open Connect <ArrowUpRight size={18} aria-hidden="true" />
            </a>
          </div>
          <div className="nf-connect-list">
            {[
              ['Gmail', 'A little less inbox.'],
              ['Calendar', 'Room for what matters.'],
              ['Notion', 'Your ideas, connected.'],
              ['Developer tools', 'Build on your own terms.']
            ].map(([title, detail], i) => (
              <div key={title}>
                <span className="nf-connect-number">0{i + 1}</span>
                <div>
                  <h3>{title}</h3>
                  <p>{detail}</p>
                </div>
                <Plus size={20} aria-hidden="true" />
              </div>
            ))}
            <a
              href={NIN_MCP_SERVER_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Explore MCP plugins <ArrowRight size={19} aria-hidden="true" />
            </a>
          </div>
        </section>
        <Lessons />
        <section
          id="contact"
          className="nf-contact nf-section"
          aria-labelledby="nf-contact-heading"
        >
          <span className="nf-eyebrow">
            THE NEXT CHAPTER STARTS WITH A CONVERSATION.
          </span>
          <div>
            <h2 id="nf-contact-heading">
              Good things
              <br />
              start with <em>hello.</em>
            </h2>
            <a
              href={mailtoSupport()}
              className="nf-contact-arrow"
              aria-label="Email NIN support"
            >
              <ArrowUpRight strokeWidth={1} aria-hidden="true" />
            </a>
          </div>
          <div className="nf-contact-links">
            <a href={mailtoSupport()}>
              Say hello <ArrowUpRight size={16} aria-hidden="true" />
            </a>
            <a href={mailtoEnterprise()}>
              For your team <ArrowUpRight size={16} aria-hidden="true" />
            </a>
            <a
              href={NIN_TELEGRAM_COMMUNITY_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Join the community <ArrowUpRight size={16} aria-hidden="true" />
            </a>
          </div>
        </section>
      </main>
      <footer className="nf-footer">
        <div className="nf-footer-top">
          <Link href="/nin" className="nf-brand" aria-label="NIN home">
            NIN<span>.</span>
          </Link>
          <p>
            Navigating India’s Next.
            <br />A sanctuary for you and your data.
          </p>
          <div>
            <Link href="/nin/models">Models</Link>
            <Link href={NIN_LEARN_URL}>Learn</Link>
            <Link href={NIN_BLOGS_URL}>Journal</Link>
            <Link href="/nin/v1">Original experience</Link>
            <a href={NIN_GITHUB_URL} target="_blank" rel="noopener noreferrer">
              GitHub
            </a>
            <a href={NIN_TWITTER_URL} target="_blank" rel="noopener noreferrer">
              X / Twitter
            </a>
            <a
              href={NIN_WEBUPI_DOCS_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              WebUPI
            </a>
          </div>
        </div>
        <div className="nf-footer-bottom">
          <span>© {new Date().getFullYear()} NIN</span>
          <span>Independent by nature.</span>
          <div>
            <Link href="/ningenie/policies/privacy-policy">Privacy</Link>
            <Link href="/ningenie/policies/terms-of-service">Terms</Link>
          </div>
        </div>
      </footer>
      <FilmPlayer open={filmOpen} onClose={() => setFilmOpen(false)} />
    </div>
  )
}
